/**
 * Outil « Triangle de Pascal » (Boîte à outils, Tle → prépa).
 *
 * On choisit une case (doigt qui glisse sur le triangle, ou compteurs n et
 * k) : les deux cases du dessus s'allument, la relation de Pascal s'écrit.
 * Mode « Chemins » : un chemin au hasard du sommet à la case, k pas à droite
 * parmi n. Mode « Impairs » : les cases impaires dessinent un triangle de
 * Sierpiński. Défi : trouver une case qui vaut une valeur donnée.
 */

import { useMemo, useRef, useState, type PointerEvent } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Stepper, ToggleChip } from '../../ui/controls';
import {
  binom,
  CHALLENGE_ROUNDS,
  parents,
  pathCells,
  pickTarget,
  randomPath,
  ROWS,
  triangle,
  type Step,
} from './pascal';

const PITCH = 30;
const ROW_H = 27;
const TOP = 17;
const W = (ROWS + 1) * PITCH;
const H = TOP + ROWS * ROW_H + 17;

const cx = (n: number, k: number) => W / 2 + (k - n / 2) * PITCH;
const cy = (n: number) => TOP + n * ROW_H;

interface Challenge {
  round: number;
  target: number;
  found: boolean;
  score: number;
}

export function PascalTool({
  onSend,
  sendCost,
  rng = Math.random,
}: ToolProps & { rng?: () => number }) {
  const [n, setN] = useState(5);
  const [k, setK] = useState(2);
  const [paths, setPaths] = useState(false);
  const [odd, setOdd] = useState(false);
  const [path, setPath] = useState<Step[] | null>(null);
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragging = useRef(false);
  const rows = useMemo(() => triangle(), []);
  const value = binom(n, k);
  const par = parents(n, k);

  const select = (nn: number, kk: number) => {
    setN(nn);
    setK(kk);
    setPath(paths ? randomPath(nn, kk, rng) : null);
    if (challenge && !challenge.found && binom(nn, kk) === challenge.target) {
      setChallenge({ ...challenge, found: true, score: challenge.score + 1 });
    }
  };

  const cellAt = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const px = ((e.clientX - r.left) / r.width) * W;
    const py = ((e.clientY - r.top) / r.height) * H;
    const nn = Math.min(ROWS, Math.max(0, Math.round((py - TOP) / ROW_H)));
    const kk = Math.min(nn, Math.max(0, Math.round((px - W / 2) / PITCH + nn / 2)));
    if (nn !== n || kk !== k) select(nn, kk);
  };

  const togglePaths = () => {
    const on = !paths;
    setPaths(on);
    setPath(on ? randomPath(n, k, rng) : null);
  };

  const startChallenge = () =>
    setChallenge({ round: 1, target: pickTarget(rng), found: false, score: 0 });
  const nextRound = () =>
    challenge &&
    setChallenge({
      round: challenge.round + 1,
      target: pickTarget(rng, challenge.target),
      found: false,
      score: challenge.score,
    });

  const isParent = (nn: number, kk: number) =>
    !!par && par.some(([pn, pk]) => pn === nn && pk === kk);
  const onPath = useMemo(() => {
    const set = new Set<string>();
    if (path) for (const [pn, pk] of pathCells(path)) set.add(`${pn}-${pk}`);
    return set;
  }, [path]);
  const rowSum = 2 ** n;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleChip on={paths} onClick={togglePaths}>
          Chemins
        </ToggleChip>
        <ToggleChip on={odd} onClick={() => setOdd((v) => !v)}>
          Impairs
        </ToggleChip>
        <ToggleChip
          on={challenge !== null}
          onClick={() => (challenge ? setChallenge(null) : startChallenge())}
        >
          Défi
        </ToggleChip>
      </div>

      {challenge && (
        <section
          aria-live="polite"
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          data-testid="pascal-challenge"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {challenge.round}/{CHALLENGE_ROUNDS} · Trouve une case qui vaut {challenge.target}.
          </p>
          {challenge.found ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">
                Trouvé : <MathText>{`$\\binom{${n}}{${k}} = ${value}$`}</MathText>
              </p>
              {challenge.round < CHALLENGE_ROUNDS ? (
                <button type="button" onClick={nextRound} className={cn(PRIMARY_BUTTON, 'ml-auto')}>
                  Valeur suivante
                </button>
              ) : (
                <>
                  <button type="button" onClick={startChallenge} className={PRIMARY_BUTTON}>
                    Rejouer
                  </button>
                  <button
                    type="button"
                    onClick={() => setChallenge(null)}
                    className={SECONDARY_BUTTON}
                  >
                    Fin du défi
                  </button>
                </>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">
              Glisse le doigt sur le triangle. Astuce : le triangle est symétrique, la plupart des
              valeurs apparaissent deux fois.
            </p>
          )}
        </section>
      )}

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          className="block w-full touch-none select-none"
          role="img"
          aria-label={`Triangle de Pascal, lignes 0 à ${ROWS}. Case choisie : ${k} parmi ${n} = ${value}.`}
          onPointerDown={(e) => {
            dragging.current = true;
            e.currentTarget.setPointerCapture?.(e.pointerId);
            cellAt(e);
          }}
          onPointerMove={(e) => {
            if (dragging.current) cellAt(e);
          }}
          onPointerUp={() => (dragging.current = false)}
          onPointerCancel={() => (dragging.current = false)}
        >
          {path && (
            <polyline
              points={pathCells(path)
                .map(([pn, pk]) => `${cx(pn, pk)},${cy(pn)}`)
                .join(' ')}
              fill="none"
              stroke="var(--color-xp-500)"
              strokeWidth={4}
              strokeLinejoin="round"
              strokeLinecap="round"
              opacity={0.7}
            />
          )}
          {rows.map((row, rn) =>
            row.map((v, rk) => {
              const sel = rn === n && rk === k;
              const parent = isParent(rn, rk);
              const isOdd = odd && v % 2 === 1;
              const fill = sel
                ? 'var(--color-primary-500)'
                : parent
                  ? 'var(--color-accent-100)'
                  : onPath.has(`${rn}-${rk}`)
                    ? 'var(--color-xp-100)'
                    : isOdd
                      ? 'var(--color-primary-100)'
                      : 'var(--color-ink-100)';
              return (
                <g key={`${rn}-${rk}`} data-testid={sel ? 'pascal-selected' : undefined}>
                  <rect
                    x={cx(rn, rk) - 14}
                    y={cy(rn) - 12}
                    width={28}
                    height={24}
                    rx={7}
                    fill={fill}
                    stroke={parent ? 'var(--color-accent-500)' : 'none'}
                    strokeWidth={2}
                  />
                  <text
                    x={cx(rn, rk)}
                    y={cy(rn) + 4.5}
                    textAnchor="middle"
                    fontSize={v >= 100 ? 11.5 : 13}
                    fontWeight={sel || parent ? 700 : 500}
                    fill={sel ? '#fff' : 'var(--color-ink-800)'}
                  >
                    {v}
                  </text>
                </g>
              );
            }),
          )}
        </svg>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Stepper
          label="n (ligne)"
          value={n}
          min={0}
          max={ROWS}
          onChange={(nn) => select(nn, Math.min(k, nn))}
        />
        <Stepper label="k (case)" value={k} min={0} max={n} onChange={(kk) => select(n, kk)} />
      </div>

      <section
        aria-live="polite"
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="pascal-relation"
      >
        <div className="flex min-h-11 items-center gap-2">
          <p className="text-ink-600 min-w-0 flex-1 text-sm font-semibold">
            {k} parmi {n} = {value}
          </p>
          {onSend && (
            <SendToSheetButton
              label={String(value)}
              cost={sendCost}
              onClick={() => onSend({ atoms: [String(value)] })}
            />
          )}
        </div>
        <MathText className="text-ink-900 block overflow-x-auto text-lg">
          {par
            ? `$\\displaystyle \\binom{${n}}{${k}} = \\binom{${n - 1}}{${k - 1}} + \\binom{${n - 1}}{${k}} = ${binom(n - 1, k - 1)} + ${binom(n - 1, k)} = ${value}$`
            : `$\\displaystyle \\binom{${n}}{${k}} = 1$`}
        </MathText>
        <p className="text-ink-600 text-sm">
          {par
            ? 'La somme des deux cases du dessus : c’est la relation de Pascal.'
            : 'Sur les bords du triangle, il n’y a qu’une façon : la case vaut 1.'}
        </p>
      </section>

      {paths && path && (
        <Note>
          <span className="font-display block font-bold tracking-widest" data-testid="pascal-path">
            {path.length ? path.join(' ') : '—'}
          </span>
          <span className="mt-1 block">
            Un chemin du sommet à cette case, c’est {n} pas dont {k} à droite (D) : il y en a{' '}
            {value}, autant que de façons de choisir {k} pas parmi {n}.
          </span>
          <button
            type="button"
            onClick={() => setPath(randomPath(n, k, rng))}
            className={cn(SECONDARY_BUTTON, 'mt-2')}
          >
            Un autre chemin
          </button>
        </Note>
      )}

      {!paths && (
        <Note live={false}>
          Somme de la ligne {n} : {rows[n]!.join(' + ')} = {rowSum} = 2<sup>{n}</sup>.
        </Note>
      )}
    </div>
  );
}
